import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { rooms, breakfast, getRoomText, getRoomById } from '../config/roomTexts';
import { useLanguage } from '../context/LanguageContext';

// Images des chambres - tableau d'images pour le carrousel
// Vous pouvez ajouter autant d'images que vous voulez pour chaque chambre
const roomImages = {
  single: ['/images/simple.jpg', '/images/simple2.jpg', '/images/simple3.jpg'],
  double: ['/images/double.jpg', '/images/double2.jpg', '/images/double3.jpg', '/images/double4.jpg', '/images/double5.jpg'],
  design: [ '/images/design2.jpg', '/images/design3.jpg', '/images/design4.jpg', '/images/design.jpg', '/images/design1.jpg'], // Exemple avec plusieurs images
  family4: ['/images/quadruple2.jpg', '/images/quadruple.jpg', '/images/quadruple3.jpg'],
  family5: ['/images/family.jpg'],
};

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/room.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px; /* Pour laisser de l'espace pour le header fixe */
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.white};
`;

const RoomsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RoomsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const RoomCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  
  &:nth-child(even) {
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const RoomImageContainer = styled.div`
  position: relative;
  height: 100%;
  min-height: 300px;
  overflow: hidden;
`;

const RoomImage = styled.div`
  height: 100%;
  min-height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  transition: opacity 0.3s ease;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.85rem;
  z-index: 2;
`;

const RoomContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RoomTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AccessibilityIcon = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  filter: brightness(0) saturate(100%) invert(35%) sepia(98%) saturate(2477%) hue-rotate(214deg) brightness(95%) contrast(91%);
`;

const RoomDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
`;

const PmrNote = styled.div`
  background-color: #eff6ff;
  border-left: 4px solid #2563eb;
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    filter: brightness(0) saturate(100%) invert(35%) sepia(98%) saturate(2477%) hue-rotate(214deg) brightness(95%) contrast(91%);
  }
  
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const RoomFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const RoomFeature = styled.li`
  padding: ${({ theme }) => theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const RoomPrice = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  small {
    font-size: 0.9rem;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textLight};
    display: block;
  }
`;

const BreakfastSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background-color: ${({ theme }) => theme.colors.light};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`;

const BreakfastContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BreakfastImage = styled.div`
  height: 300px;
  background-image: url('/images/breakfast.jpg');
  background-size: cover;
  background-position: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 1;
  }
`;

const BreakfastContent = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 2;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const BreakfastTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
`;

const BreakfastDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  font-size: 1.1rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BreakfastPrice = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const RoomCarousel = ({ images, roomId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  if (images.length === 0) return null;
  
  return (
    <RoomImageContainer>
      <RoomImage image={images[currentIndex]} />
      {images.length > 1 && (
        <>
          <CarouselButton direction="left" onClick={prevImage}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
            </svg>
          </CarouselButton>
          <CarouselButton direction="right" onClick={nextImage}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </CarouselButton>
          <ImageCounter>{currentIndex + 1} / {images.length}</ImageCounter>
        </>
      )}
    </RoomImageContainer>
  );
};

const Rooms = () => {
  const { language } = useLanguage();
  const breakfastText = getRoomText(language, 'breakfast') || breakfast;

  // Effet pour gérer le défilement automatique vers l'ancre
  useEffect(() => {
    // Récupérer l'ancre de l'URL (sans le #)
    const hash = window.location.hash.substring(1);
    
    if (hash) {
      // Attendre un peu que le DOM soit complètement chargé
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Calculer la position de défilement en tenant compte du header fixe (70px)
          const headerOffset = 80; // Un peu plus que la hauteur du header pour avoir de l'espace
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          // Faire défiler la page
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  
  return (
    <>
      <PageHeader>
        <PageTitle>{getRoomText(language, 'pageTitle')}</PageTitle>
      </PageHeader>
      
      <RoomsSection>
        <RoomsContainer>
          {rooms.map((room) => {
            const translatedRoom = getRoomById(language, room.id);
            return (
              <RoomCard key={room.id} id={room.id}>
                <RoomCarousel images={roomImages[room.id]} roomId={room.id} />
                <RoomContent>
                  <RoomTitle>
                    {translatedRoom?.title || room.title}
                    {room.id === 'design' && (
                      <AccessibilityIcon src="/images/pmr.svg" alt="PMR" />
                    )}
                  </RoomTitle>
                  <RoomDescription>{translatedRoom?.description || room.description}</RoomDescription>
                  
                  {translatedRoom?.pmrNote && (
                    <PmrNote>
                      <img src="/images/pmr.svg" alt="PMR" />
                      <p>{translatedRoom.pmrNote}</p>
                    </PmrNote>
                  )}
                  
                  <RoomFeatures>
                    {(translatedRoom?.features || room.features).map((feature, index) => (
                      <RoomFeature key={index}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        {feature}
                      </RoomFeature>
                    ))}
                  </RoomFeatures>
                  
                  <RoomPrice>
                    <div>
                      {getRoomText(language, 'pricePrefix')} {room.price}€
                      <small>{getRoomText(language, 'taxInfo').replace('{taxe}', room.taxe)}</small>
                    </div>
                  </RoomPrice>
                </RoomContent>
              </RoomCard>
            );
          })}
        </RoomsContainer>
      </RoomsSection>
      
      <BreakfastSection>
        <BreakfastContainer>
          <BreakfastContent>
            <BreakfastTitle>{breakfastText.title}</BreakfastTitle>
            <BreakfastDescription>{breakfastText.description}</BreakfastDescription>
            <BreakfastPrice>{breakfastText.priceText}</BreakfastPrice>
          </BreakfastContent>
          <BreakfastImage />
        </BreakfastContainer>
      </BreakfastSection>
    </>
  );
};

export default Rooms;
